/* istanbul ignore file */

import React, {Component} from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import Mousetrap from 'mousetrap'
import Animate from 'rc-animate'
import bowser from 'bowser'
import {AutoSizer} from 'react-virtualized'
import {noop, debounce, get} from 'lodash'
import {compose, pure, withState, withPropsOnChange} from 'recompose'
import cookie from 'cookie'
import {wait} from 'powtoon-commons/deferredOperations'
import {setUserIdentity, createLogger} from './logging'
import styles from './Player.scss'
import {getPlayerPermissions} from './licensing'
import {adjustedCurrentSlideTotalTime} from './utils'
import Overlay from './components/Overlay'
import Controls from './components/ControlsBar/Controls'
import FullscreenToggle from './components/FullscreenToggle'
import Preloader from './components/Preloader'
import {getCorePlayerImports} from 'powtoon-html-player/src/CorePlayerImports'
import events from 'powtoon-commons/services/events'
import webvarsConfigurationService from './services/webvarsConfigurationService'
import '@powtoon/design-system-components/dist/global.css'
const logger = createLogger('html-player')

const documentMousetrapInstance = new Mousetrap()

const brandingStyle = webvarsConfigurationService.getWebVar('brandingStyle')

const HIDE_CONTROLS_BAR_TIMEOUT = 2000
const SLIDE_WIDTH = 1024
const SLIDE_HEIGHT = 576
const AUDIO_STATE_SUSPENDED_STATE = 'suspended'
const AUDIO_STATE_RUNNING_STATE = 'running'

const isIdleCallbackSupported = !!window.requestIdleCallback
const docUpdateDelay = 5000

const fadeEffect = {
  enter: styles['fade-enter'],
  enterActive: styles['fade-enter-active'],
  leave: styles['fade-leave'],
  leaveActive: styles['fade-leave-active']
}

const IS_ANDROID = !(bowser.mac || bowser.ios) && bowser.mobile
const IS_IOS = (bowser.mac || bowser.ios) && bowser.mobile
const D_KEY_CODE_L = 76
const D_KEY_CODE_K = 75
const SCRUB_STATE = {
  IDLE: 'idle',
  UPDATING: 'updating',
  SCRUBING: 'scrubing'
}

const performanceFromLoadTime = 'start of navigation till start download core Player'
const performanceInitCorePlayer = 'html player measure core player init'

export const BUILD_IDENTIFIER = process.env.BUILD_IDENTIFIER

const shortcuts = [
  {
    keys: 'up',
    handler(component) {
      component.controlsBar.volume = Math.min(1, component.controlsBar.volume + 0.05)
    }
  },
  {
    keys: 'down',
    handler(component) {
      component.controlsBar.volume = Math.max(0, component.controlsBar.volume - 0.05)
    }
  },
  {
    keys: ['left', 'pageup'],
    handler(component) {
      const {slideshowMode, isDisableMovieControls} = component.props
      const {currentTime} = component

      if (!isDisableMovieControls)
      {
        if (slideshowMode) {
          component.slideshowControls.previousSlide()
        } else {
          component.changeAbsoluteTime(Math.max(0, currentTime - 5))
        }
      }
    }
  },
  {
    keys: ['right', 'pagedown'],
    handler(component) {
      const {slideshowMode, isDisableMovieControls} = component.props
      const {currentTime} = component
      if (!isDisableMovieControls){
        if (slideshowMode) {
          component.slideshowControls.playToNextHold()
        } else {
          component.getPowtoonDuration().then(duration => {
            component.changeAbsoluteTime(Math.min(duration, currentTime + 5))
          })
        }
      }
    }
  },
  {
    keys: ['f', 'F'],
    handler(component) {
      const {isDisableMovieControls} = component.props
      if (!isDisableMovieControls){
        component.toggleFullScreen()
      }
    }
  },
  {
    keys: ['m', 'M'],
    handler(component) {
      component.controlsBar.toggleMute()
    }
  },
  {
    keys: 'home',
    handler(component) {
      const {isDisableMovieControls} = component.props
      if (!isDisableMovieControls){
        component.jumpToStart()
      }
    }
  },
  {
    keys: 'space',
    handler(component) {
      const {isDisableMovieControls} = component.props
      if (!isDisableMovieControls){
        component.togglePlay()
      }
    }
  }
]

@compose(
  pure,
  withState('scale', 'setScale', {scaleX: 1, scaleY: 1}),
  withPropsOnChange(['scale'], ({scale: {scaleX, scaleY}}) => ({
    corePlayerStyle: {
      width: `${SLIDE_WIDTH}px`,
      height: `${SLIDE_HEIGHT}px`,
      transform: `scale(${scaleX || 1}, ${scaleY || 1})`,
      transformOrigin: '0 0'
    }
  }))
)
export default class Player extends Component {
  static propTypes = {
    doc: PropTypes.object,
    permissions: PropTypes.object,
    playerData: PropTypes.object,
    slideshowMode: PropTypes.bool.isRequired,
    autoplay: PropTypes.bool,
    hideControls: PropTypes.bool.isRequired,
    hideBlackBorders: PropTypes.bool.isRequired,
    isInStudio: PropTypes.bool,
    showSpinnerWhenLoading: PropTypes.bool,
    onPlayerReady: PropTypes.func.isRequired,
    onPlayerInit: PropTypes.func.isRequired,
    onPlay: PropTypes.func.isRequired,
    onPause: PropTypes.func.isRequired,
    onToggleMute: PropTypes.func,
    onSlideChange: PropTypes.func.isRequired,
    onTimeChange: PropTypes.func.isRequired,
    onTotalTimeChanged: PropTypes.func,
    onLinkDisplayed: PropTypes.func,
    onLinkClicked: PropTypes.func,
    onEnd: PropTypes.func.isRequired,
    onDocUpdateStart: PropTypes.func.isRequired,
    onDocUpdateFinish: PropTypes.func.isRequired,
    dispatchWindowEvents: PropTypes.func,
    hideBranding: PropTypes.bool,
    enableBrandingWhenDocumentHasLocks: PropTypes.bool,
    isShortcutsActive: PropTypes.bool,
    killOnWindowUnload: PropTypes.bool,
    setCorePlayerUpdateCounter: PropTypes.func.isRequired,
    setNewPlayerData: PropTypes.func,
    performanceTracker: PropTypes.object,
    onCorePlayerUpdateStatusChanged: PropTypes.func,
    isDraftPlayEnabled: PropTypes.bool,
    initialCaptionLocale: PropTypes.string,
    locales: PropTypes.array
  }

  static defaultProps = {
    slideshowMode: false,
    autoplay: false,
    hideControls: false,
    hideBlackBorders: false,
    isInStudio: false,
    showSpinnerWhenLoading: true,
    onPlayerInit: noop,
    onPlayerReady: noop,
    onPlay: noop,
    onPause: noop,
    onToggleMute: noop,
    onSlideChange: noop,
    onTimeChange: noop,
    onTotalTimeChanged: noop,
    onLinkDisplayed: noop,
    onLinkClicked: noop,
    onEnd: noop,
    onDocUpdateStart: noop,
    onDocUpdateFinish: noop,
    dispatchWindowEvents: noop,
    hideBranding: false,
    enableBrandingWhenDocumentHasLocks: true,
    isShortcutsActive: true,
    killOnWindowUnload: true,
    setCorePlayerUpdateCounter: noop,
    setNewPlayerData: noop,
    performanceTracker: undefined,
    onCorePlayerUpdateStatusChanged: noop,
    isDraftPlayEnabled: false,
    initialCaptionLocale: null
  }

  constructor(props) {
    super(props)
    this.isDocDirty = true
    this.isEnd = false
    this.currentTime = 0
    this.currentSlideTime = 0
    this.currentSlideIndex = 0
    this.shortcutsHandlers = new Map()
    this.pendingCorePlayerActionsQueue = []
    this.activeOutGoingEventLoggs = false
    this.activeInComeingEventLoggs = false
    this.shouldPlayAfterUpdate = false
    this.scrubState = SCRUB_STATE.IDLE
    this.fullscreen = React.createRef()
    this.corePlayerContainerRef = React.createRef()
    this.initCorePlayerOccurred = false
    this.performanceTracker = props.performanceTracker
    this.state = {
      size: props.size ?
        {
          width: props.size.width,
          height: props.size.height,
          margin: 0
        } :
        undefined,
      corePlayerConnectionInstance: null,
      corePlayerReadyPromise: null,
      showPreloader: true,
      isUpdating: false,
      fullscreenEnabled: true,
      phoneRotateAnimationVisible: false,
      isFullscreen: false,
      showControlsBar: true,
      slidesDurations: [],
      isPlaying: false,
      isOverlayVisible: false
    }
    this.loadCorePlayerScript()
    this.updateSlidesDuration(props)
  }

  loadCorePlayerScript() {
    this.playerReportedReady = false

    const loadScript = url => {
      const script = document.createElement('script')
      script.src = url
      script.async = false
      document.body.appendChild(script)
    }

    const playerLoadTimer = () => {
      this.playerWatchdog = setTimeout(() => {
        if (!this.playerReportedReady) {
          logger.error(new Error('Player did not report ready for 10 minutes after init.'))
        }
        clearTimeout(this.playerWatchdog)
      }, 10 * 60 * 1000) // 10 minutes
    }

    const {ENTRY_POINT_NAME, NODE_ENV, API_HOST, CORE_PLAYER_CDN_PATH} = process.env

    const corePlayerVersion = this.props.corePlayerVersion || window.app_versions.core_player

    this.corePlayerURL = `${CORE_PLAYER_CDN_PATH}${corePlayerVersion}/corePlayer.js`

    logger.log('***** HTML-PLAYER STARTED DOWNLOAD CORE-PLAYER **** ', {
      ENTRY_POINT_NAME,
      NODE_ENV,
      API_HOST,
      CORE_PLAYER_URL: this.corePlayerURL
    })

    this.corePlayerCreatedTime = new Date()
    this.performanceTracker.operationMeasurement({
      measureFromStartOfNavigationTillNow: true,
      operationLabel: performanceFromLoadTime,
      performanceEventID: null
    })
    loadScript(this.corePlayerURL)
    playerLoadTimer()
  }

  toggleLoggs = evt => {
    if (evt.keyCode === D_KEY_CODE_L && (evt.ctrlKey || evt.metaKey) && evt.altKey) {
      evt.preventDefault()

      if (!this.activeOutGoingEventLoggs) {
        this.activeOutGoingEventLoggs = true
        console.info('<<< html-player will log outgoing functions to CorePlayerConnection true')
      } else {
        this.activeOutGoingEventLoggs = false
        console.info('<<< html-player will log outgoing functions to CorePlayerConnection false')
      }
    }
    if (evt.keyCode === D_KEY_CODE_K && (evt.ctrlKey || evt.metaKey) && evt.altKey) {
      evt.preventDefault()

      if (!this.activeInComeingEventLoggs) {
        this.activeInComeingEventLoggs = true
        console.info('>>> html-player will log incoming functions from CorePlayerConnection true')
      } else {
        this.activeInComeingEventLoggs = false
        console.info('>>> html-player will log incoming functions from CorePlayerConnection false')
      }
    }
  }
  debugHtmlPlayerEventLog(funcName, ...paramsArray) {
    if (this.activeOutGoingEventLoggs) {
      //we dont want this logs reach the server
      //will be activated with combination of ctrl + alt + L
      console.info('<<< html-player will invoke CorePlayerConnection with func', funcName, ...paramsArray)
    }
  }

  debugCorePlayerEventLog(funcName, additionalInfo) {
    if (this.activeInComeingEventLoggs) {
      //we dont want this logs reach the server
      //will be activated with combination of ctrl + alt + K
      console.info(`>>> html-player handle message from corePlayer ${funcName}`, additionalInfo)
    }
  }

  initCorePlayer = async () => {
    this.performanceMeasureInitCorePlayer =this.logDocUpdateEnd = this.performanceTracker.operationMeasurement({
      operationLabel: performanceInitCorePlayer,
      performanceEventID: 50348
    })

    if (!this.corePlayerContainerRef.current) {
      return
    }
    const {CorePlayerConnectionCtor} = await getCorePlayerImports()
    logger.log('core player exposed exports')

    const {
      doc,
      isInStudio,
      permissions,
      watermarkOnExport,
      onPlayerInit,
      apiHost,
      dispatchWindowEvents,
      hideBranding,
      enableBrandingWhenDocumentHasLocks,
      playerData,
      isDraftPlayEnabled,
      initialCaptionLocale
    } = this.props
    const {isFullscreen, size} = this.state
    const playerPermissions = getPlayerPermissions(
      doc,
      permissions,
      {watermarkOnExport, hideBranding, enableBrandingWhenDocumentHasLocks}
    )

    logger.debug('@@@ initCorePlayer playerPermissions', playerPermissions)

    events.on('studio-preview-open', loggerTimeFunc => {
      this.previewOpenPerformance = loggerTimeFunc
    })

    const corePlayerReadyPromise = new Promise(resolve => {
      if (!size) {
        logger.warn('Player is being initialized with zero-size!')
      }
      const {scaleX, scaleY, stateUpdate} = this.recalcCorePlayerSizeParams(isFullscreen, size)
      console.log(`%c ///////////// Creating player with Scale (${scaleX}, ${scaleY})`, 'background: orange')
      const corePlayerConnectionInstance = new CorePlayerConnectionCtor(
        this.corePlayerContainerRef.current,
        doc,
        playerPermissions,
        {
          brandingStyle,
          onSlideChange: currentSlideIndex => {
            this.debugCorePlayerEventLog('onSlideChange', currentSlideIndex)

            const {isPlaying} = this.state
            this.currentSlideIndex = currentSlideIndex
            if (this.slideshowControls) {
              this.slideshowControls.setCurrentSlide(currentSlideIndex)
            }
            if (isPlaying) {
              this.props.onSlideChange(currentSlideIndex)
              dispatchWindowEvents(
                new CustomEvent('powtoon-player-event', {detail: {type: 'slide-change', currentSlideIndex}})
              )
            }
          },
          onTimeChange: currentSlideTime => {
            this.debugCorePlayerEventLog('onTimeChange', currentSlideTime)

            const {isPlaying} = this.state
            if (isPlaying) {
              this.currentSlideTime = currentSlideTime
              this.isOverlayVisible = false
              dispatchWindowEvents(
                new CustomEvent('powtoon-player-event', {detail: {type: 'time-change', currentSlideTime}})
              )
            }
          },
          onTotalTimeChanged: currentTime => {
            this.debugCorePlayerEventLog('onTotalTimeChanged ', currentTime)

            const {hideControls} = this.props
            const {isPlaying} = this.state
            this.isEnd = false
            this.currentTime = currentTime
            dispatchWindowEvents(
              new CustomEvent('powtoon-player-event', {detail: {type: 'total-time-change', currentTime}})
            )

            if (!hideControls && this.movieControls && isPlaying) {
              this.movieControls.updateTotalTime(currentTime)
            }
          },
          onPlay: () => {
            this.debugCorePlayerEventLog('onPlay')

            if (this.previewOpenPerformance) {
              this.previewOpenPerformance()
              this.previewOpenPerformance = null
            }

            const {hideControls} = this.props
            this.isEnd = false
            this.setState({showPreloader: false, isManualPause: false, isPlaying: true}, () => {
              dispatchWindowEvents(new CustomEvent('powtoon-player-event', {detail: {type: 'play'}}))
              if (this.onPlayResolve) {
                this.onPlayResolve()
              }
              else {
                logger.info(new Error('not exist onPlayResolve'))
              }
              this.props.onPlay()
              if (!hideControls && this.movieControls) {
                this.movieControls.updateIsPlay(true)
              }
            })
          },
          onPause: () => {
            this.debugCorePlayerEventLog('onPause')

            const {isEnd, currentSlideTime, currentTime} = this
            const {isPlaying} = this.state
            const additionalState = {}
            if (!isEnd && !this.props.isInStudio) {
              additionalState.isOverlayVisible = !this.props.slideshowMode
            }

            const newState = {isPlaying: false, audioState: AUDIO_STATE_RUNNING_STATE, ...additionalState}
            this.setState(newState, () => {
              dispatchWindowEvents(new CustomEvent('powtoon-player-event', {detail: {type: 'pause'}}))
            })

            if (isPlaying) {
              this.props.onPause(currentSlideTime, currentTime)
            }
          },
          onIsPlayerReady: (corePlayerUpdateCounter, corePlayerUpdateStatus) => {
            this.debugCorePlayerEventLog('onIsPlayerReady', corePlayerUpdateCounter)
            const {
              slideshowMode, onPlayerReady, autoplay, setCorePlayerUpdateCounter, onCorePlayerUpdateStatusChanged
            } = this.props

            setCorePlayerUpdateCounter(corePlayerUpdateCounter)

            this.playerReportedReady = true
            clearTimeout(this.playerWatchdog)

            if (this.state.size) {
              this.onResize(this.state.size)
            }

            if (this.corePlayerCreatedTime) {
              const duration = new Date() - this.corePlayerCreatedTime
              const {ENTRY_POINT_NAME, NODE_ENV, API_HOST} = process.env
              this.performanceMeasureInitCorePlayer()
              logger.log('***** HTML-PLAYER , CORE-PLAYER READY **** ', {
                ENTRY_POINT_NAME,
                NODE_ENV,
                API_HOST,
                CORE_PLAYER_URL: this.corePlayerURL,
                DURATION: duration
              })

              delete this.corePlayerCreatedTime
              delete this.corePlayerURL
            }
            onCorePlayerUpdateStatusChanged(corePlayerUpdateStatus)

            if (slideshowMode) {
              corePlayerConnectionInstance.setSlideshowMode(true)
            }

            this.currentSlideIndex = corePlayerConnectionInstance.currentSlide
            if (this.slideshowControls) {
              this.slideshowControls.setCurrentSlide(this.currentSlideIndex)
            }

            const isCorePlayerDraft = !isDraftPlayEnabled && !corePlayerUpdateStatus.isReady
            this.setState({showPreloader: isCorePlayerDraft}, () => {
              dispatchWindowEvents(new CustomEvent('powtoon-player-event', {detail: {type: 'player-ready'}}))
              onPlayerReady()
            })

            if (autoplay && !isCorePlayerDraft) {
              this.play()
            }

            this.processCorePlayerActionsQueue(corePlayerConnectionInstance)

            const {slides, manifest, transitions} = this.props.doc.content.powtoon
            const userType = this.props.permissions.licenseLevel
            const numberOfSlides = slides.length
            const numberOfTransitions = Object.keys(transitions).length
            const itemTypes = {}
            const manifestArray = Object.keys(manifest).map(key => manifest[key])
            manifestArray.forEach(manifestType => {
              const manifestTypeArray = Object.keys(manifestType).map(key => manifestType[key])
              manifestTypeArray.forEach(manifestItem => {
                const assetItemTypeKey = `NumberOfItems_${manifestItem.itemType}`
                if (itemTypes[assetItemTypeKey]) {
                  itemTypes[assetItemTypeKey]++
                } else {
                  itemTypes[assetItemTypeKey] = 1
                }
              })
            })
            const isTTR = window.performance && performance.navigation.type === 1
            const operationLabel = isTTR
              ? 'TTR Time for reload studio loaded and ready to play'
              : 'TTI Time to initial load studio loaded and ready to play'
            const performanceEventID = isTTR ? 50349: 50350

            this.performanceTracker.operationMeasurement({
              measureFromStartOfNavigationTillNow: true,
              extraParams: {
                numberOfSlides,
                numberOfTransitions,
                ...itemTypes,
                userType
              },
              operationLabel,
              performanceEventID
            })

            resolve(corePlayerConnectionInstance)
          },
          onIsPlayerUpdated: (corePlayerUpdateCounter, corePlayerUpdateStatus) => {
            const {setCorePlayerUpdateCounter, onCorePlayerUpdateStatusChanged} = this.props

            this.debugCorePlayerEventLog('onIsPlayerUpdated', corePlayerUpdateCounter)
            setCorePlayerUpdateCounter(corePlayerUpdateCounter)

            this.setIsUpdating(false, () => {
              dispatchWindowEvents(new CustomEvent('powtoon-player-event', {detail: {type: 'doc-update-finish'}}))
              this.props.onDocUpdateFinish()
            })
            onCorePlayerUpdateStatusChanged(corePlayerUpdateStatus)

            if (this.logDocUpdateEnd) {
              this.logDocUpdateEnd()
            }
            this.logDocUpdateEnd = null
            this.playAfterUpdate()

            const isCorePlayerDraft = !isDraftPlayEnabled && !corePlayerUpdateStatus.isReady
            this.setState({showPreloader: isCorePlayerDraft})
          },
          onFinishedPlayingCallback: () => {
            this.debugCorePlayerEventLog('onFinishedPlayingCallback')

            const {hideControls} = this.props
            this.isEnd = true
            this.props.onTotalTimeChanged(this.currentTime)

            this.setState({isPlaying: false}, () => {
              dispatchWindowEvents(new CustomEvent('powtoon-player-event', {detail: {type: 'end'}}))
              this.props.onEnd()
            })
            if (!hideControls && this.movieControls) {
              this.movieControls.updateIsPlay(false)
            }
          },
          onMetaDataReady: ({slidesDurations}) => {
            this.debugCorePlayerEventLog('onMetaDataReady ', slidesDurations)

            this.setState({slidesDurations})
          },
          onPlayerAction: ({type}) => {
            this.debugCorePlayerEventLog('onPlayerAction ', type)

            if (type !== 'click') {
              return
            }
            this.togglePlay()
          },
          setPlayerData: (setPlayerDataResponse) => {
            this.debugCorePlayerEventLog('setPlayerData ', setPlayerDataResponse)
            //expected payload {data:{newPlayerData},acknowledged: [id1, id2]}
            this.props.setNewPlayerData(setPlayerDataResponse)
          },
          onLinkClicked: this.props.onLinkClicked,
          onLinkDisplayed: this.props.onLinkDisplayed,
          dev: (apiHost && apiHost.split('//')[1].split('.')[0]) || window.location.hostname.split('.')[0],
          isInStudio,
          getDocument: doc,
          studioPowtoonHash: this.props.studioUpdateCounter,
          scaleX,
          scaleY,
          enableRichText: doc.enableRichText || false,
          playerData,
          activeCaptionLocale: initialCaptionLocale
        }
      )

      this.props.setScale({scaleX, scaleY})
      this.setState(stateUpdate)
      this.setState({corePlayerConnectionInstance, isHorizontal: false})
      window.CorePlayer.corePlayerConnection = corePlayerConnectionInstance
    })

    this.setState({corePlayerReadyPromise})
    window.CorePlayer.corePlayerReadyPromise = corePlayerReadyPromise

    dispatchWindowEvents(new CustomEvent('powtoon-player-event', {detail: {type: 'player-init'}}))
    onPlayerInit()
  }

  togglePlay = () => {
    const {slideshowMode} = this.props
    const {isPlaying} = this.state
    const {isEnd} = this
    if (isPlaying) {
      this.pause({isManualPause: true})
    } else if (slideshowMode) {
      if (isEnd) {
        this.jumpToStart(0)
      }
      this.playToNextHold()
    } else {
      if (isEnd) {
        this.restartPlayback()
      } else {
        this.play()
      }
    }
  }

  componentDidMount() {
    const {innerRef, killOnWindowUnload} = this.props

    this._isMounted = true

    if (innerRef) {
      innerRef(this)
    }

    if (this.fullscreen) {
      this.setState({fullscreenEnabled: this.fullscreen.current.isEnabled()})
    }

    shortcuts.forEach(({keys, handler}) => {
      const callback = () => {
        const {hideControls, isShortcutsActive} = this.props

        if (hideControls || !isShortcutsActive) {
          return
        }

        handler(this)
      }

      this.shortcutsHandlers.set(keys, callback)
      documentMousetrapInstance.bind(keys, callback)
      document.addEventListener('keydown', this.toggleLoggs, false)
    })

    this.onCorePlayerReady(CPC => {
      this.setState({audioState: CPC.audioState})
    }, 'audioState')

    document.addEventListener('visibilitychange', this.handleVisibilityChange)

    if (killOnWindowUnload) {
      window.addEventListener('beforeunload', this.handleWindowBeforeUnload)
    }
  }

  componentWillUnmount() {
    this._isMounted = false
    logger.warn('HTML Player unmounted!')
    this.onCorePlayerReady(CPC => CPC.kill(), 'kill')
    this.shortcutsHandlers.forEach((callback, key) => documentMousetrapInstance.unbind(key, callback))
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    window.removeEventListener('beforeunload', this.handleWindowBeforeUnload)
    document.removeEventListener('keydown', this.toggleLoggs, false)
    this.cancelScheduledDocUpdates()
  }

  componentDidUpdate(prevProps, prevState) {
    const {corePlayerConnectionInstance, isFullscreen} = this.state
    const {slideshowMode, doc, permissions, playerData, size, isForceUpdate, initialCaptionLocale} = this.props
    const {
      slideshowMode: prevSlideshowMode = false,
      doc: prevDoc,
      permissions: prevPermissions,
      size: prevSize,
      playerData: prevPlayerData,
      isForceUpdate: prevIsForceUpdate,
      initialCaptionLocale: prevInitialCaptionLocale
    } = prevProps

    const userId = permissions && permissions.id
    const prevUserId = prevPermissions && prevPermissions.id
    const powtoonId = doc && doc.content.powtoonId
    const prevPowtoonId = prevDoc && prevDoc.content.powtoonId
    const powtoon = doc && doc.content && doc.content.powtoon
    const prevPowtoon = prevDoc && prevDoc.content && prevDoc.content.powtoon
    const width = size && size.width
    const prevWidth = prevSize && prevSize.width
    const height = size && size.height
    const prevHeight = prevSize && prevSize.height

    let initOccurredNow = false
    if (doc) {
      if (!this.initCorePlayerOccurred && permissions) {
        this.initCorePlayerOccurred = true
        this.initCorePlayer()
        initOccurredNow = true
      }

      if (!prevDoc) {
        this.updateSlidesDuration(this.props)
      }
    }

    if (!initOccurredNow && !isFullscreen && (width !== prevWidth || height !== prevHeight)) {
      this.onResize({
        width: size.width,
        height: size.height
      })
    }
    if (!corePlayerConnectionInstance) {
      return
    }

    if (prevSlideshowMode !== slideshowMode) {
      corePlayerConnectionInstance.setSlideshowMode(slideshowMode)
      this.pause()
    }

    if ((isForceUpdate !== prevIsForceUpdate) || (initialCaptionLocale !== prevInitialCaptionLocale)) {
      this.setActiveCaptionLocale(initialCaptionLocale)
    }

    if (doc !== prevDoc &&
      ( powtoon !== prevPowtoon ||
        permissions !== prevPermissions ||
        playerData !== prevPlayerData
      )) {
      this.isDocDirty = true
      if (isForceUpdate) {
        this.updatePlayer()
      } else {
        this.lazyUpdatePlayer()
      }
    }

    const isIdentityChanged = (userId !== prevUserId && prevUserId) || powtoonId !== prevPowtoonId

    if (isIdentityChanged) {
      const {sessionid: sessionId, visitorid: visitorId} = cookie.parse(document.cookie)
      setUserIdentity({userId, powtoonId, sessionId, visitorId})
    }
  }

  processCorePlayerActionsQueue(CPC) {
    if (CPC && this.pendingCorePlayerActionsQueue && this.pendingCorePlayerActionsQueue.length > 0) {
      this.pendingCorePlayerActionsQueue.forEach(item => {
        this.onCorePlayerReady(item.func, `processCorePlayerActionsQueue ${ item.name}`)
      })
      this.pendingCorePlayerActionsQueue = []
    }
  }

  onCorePlayerReady(func, funcNameForLog, paramsArray = []) {
    /**
     * using defer wait we shift callback execution to the regular event loop flow,
     * cause Promise callbacks have higher than others priority.
     * So the heavy code would be executed with higher priority, but we expect it later.
     */
    return wait().then(() => {
      const {corePlayerReadyPromise} = this.state

      if (corePlayerReadyPromise) {
        this.debugHtmlPlayerEventLog(funcNameForLog, paramsArray)
        logger.info(`html player to core player ${funcNameForLog} `, paramsArray)
        return corePlayerReadyPromise.then(func)
      } else {
        // corePlayerReadyPromise was not created yet...
        // studio is up and running, html-player started, but core player bundle is not ready yet.
        // We dont want to miss user actions so lets stack the missed events and use it once player arrives.
        this.pendingCorePlayerActionsQueue.push({func, name: funcNameForLog})
      }
      return null
    })
  }

  setActiveCaptionLocale = (locale) => {
    const {corePlayerConnectionInstance} = this.state
    corePlayerConnectionInstance?.setActiveCaptionLocale?.(locale)
  }

  setIsUpdating = (isUpdating, callbackFunc = null) => {
    this.setState({isUpdating}, callbackFunc)
    if (!isUpdating && this.pendingCorePlayerActionsQueue) {
      const {corePlayerConnectionInstance} = this.state
      return this.processCorePlayerActionsQueue(corePlayerConnectionInstance)
    }
  }

  updatePlayerDebounced = debounce(() => {
    if (isIdleCallbackSupported) {
      window.cancelIdleCallback(this._idleCallback)
      this._idleCallback = window.requestIdleCallback(this.updatePlayer, {timeout: docUpdateDelay})
    } else {
      this.updatePlayer()
    }
  }, docUpdateDelay)

  lazyUpdatePlayer() {
    //this.isDocDirty = true
    this.updatePlayerDebounced()
  }

  cancelScheduledDocUpdates() {
    this.updatePlayerDebounced.cancel()

    if (isIdleCallbackSupported) {
      window.cancelIdleCallback(this._idleCallback)
    }
  }

  updateSlidesDuration = props => {
    const slides = get(props, 'doc.content.powtoon.slides', [])
    this.numberOfSlides = slides.length
    this.totalDuration = slides.reduce((accumulator, currentSlide) => accumulator + currentSlide.duration, 0)
  }

  updatePlayer = ({shouldPlayAfterUpdate = false} = {}) =>
    new Promise(resolve => {
      const {
        watermarkOnExport,
        doc,
        onDocUpdateStart,
        permissions,
        dispatchWindowEvents,
        hideBranding,
        studioUpdateCounter,
        enableBrandingWhenDocumentHasLocks,
        playerData
      } = this.props

      this.logDocUpdateEnd = this.performanceTracker.operationMeasurement({
        operationLabel: 'studio update core player with document change',
        performanceEventID: null
      })
      this.cancelScheduledDocUpdates()

      this.updateSlidesDuration(this.props)

      if (!this.isDocDirty) {
        this.shouldPlayAfterUpdate = shouldPlayAfterUpdate
        this.playAfterUpdate()
        return resolve()
      }

      dispatchWindowEvents(
        new CustomEvent('powtoon-player-event', {
          detail: {type: 'doc-update-start'}
        })
      )

      if (!this._isMounted) {
        resolve()
        return
      }

      const playerPermissions = getPlayerPermissions(
        doc,
        permissions,
        {watermarkOnExport, hideBranding, enableBrandingWhenDocumentHasLocks}

      )

      logger.debug('@@@ updatePlayer playerPermissions', playerPermissions)

      this.shouldPlayAfterUpdate = shouldPlayAfterUpdate
      this.setState({showPreloader: true})
      this.onCorePlayerReady(CPC => {
        onDocUpdateStart()
        CPC.updateDoc(doc, playerPermissions, studioUpdateCounter, playerData)
        this.isDocDirty = false
        resolve()
      }, 'updatePlayer')
    })

  play = () => {
    const {isFullscreen} = this.state
    logger.debug('play', {isFullscreen})
    return Promise.all([this.createOnPlayResolver(), this.updatePlayer({shouldPlayAfterUpdate: true})])
  }

  paramsForPlayer = null
  onPlayResolve = null

  createOnPlayResolver = () => new Promise(onPlayResolve => this.onPlayResolve = onPlayResolve)

  playWithParams = ({slideIndex, playFromTime, stopOnSlideEnd}) => {
    logger.debug('play with params', {slideIndex, playFromTime})

    this.paramsForPlayer = {
      slideIndex,
      playFromTime,
      stopOnSlideEnd
    }

    return Promise.all([this.createOnPlayResolver(), this.updatePlayer({shouldPlayAfterUpdate: true})])
  }

  playAfterUpdate = () => {
    if (this.shouldPlayAfterUpdate) {
      this.onCorePlayerReady(
        CPC => {
          if (this.paramsForPlayer) {
            CPC.playWithParams(this.paramsForPlayer)
          } else {
            CPC.play()
          }
          this.paramsForPlayer = null
          this.shouldPlayAfterUpdate = false
        },
        this.paramsForPlayer ? 'playWithParams' : 'play',
        [...this.paramsForPlayer]
      )
    }
  }

  playToNextHold = () =>
    this.onCorePlayerReady(CPC => {
      logger.log('play to next hold')
      this.updatePlayer().then(() => CPC.playToNextHold())
    }, 'playToNextHold')

  pause = ({isManualPause} = {}) => {
    const {isEnd, currentSlideTime, currentTime} = this
    const {isPlaying} = this.state
    const {dispatchWindowEvents, slideshowMode} = this.props

    this.setShowControlsBar()
    this.shouldPlayAfterUpdate = false
    if (isPlaying) {
      const additionalState = !isEnd && !slideshowMode ? {isOverlayVisible: true} : {}
      this.setState({isPlaying: false, isManualPause, ...additionalState}, () => {
        dispatchWindowEvents(new CustomEvent('powtoon-player-event', {detail: {type: 'end'}}))
        this.props.onEnd(currentSlideTime, currentTime)
      })

      return this.onCorePlayerReady(CPC => CPC.pause(), 'pause')
    }

    return Promise.resolve()
  }

  changeAbsoluteTime = t =>
    this.onCorePlayerReady(CPC => {
      return CPC.changeAbsoluteTime(t), 'changeAbsoluteTime', [t]
    }, 'changeAbsoluteTime', t)

  jumpToStart = () => this.changeAbsoluteTime(0)
  jumpToNextHold = () => this.onCorePlayerReady(CPC => CPC.jumpToNextHold(), 'jumpToNextHold')
  jumpToPreviousHold = () => this.onCorePlayerReady(CPC => CPC.jumpToPreviousHold(), 'jumpToPreviousHold')
  jumpToNextSlide = () => this.onCorePlayerReady(CPC => CPC.jumpToNextSlide(), 'jumpToNextSlide')
  jumpToPreviousSlide = () => this.onCorePlayerReady(CPC => CPC.jumpToPreviousSlide(), 'jumpToPreviousSlide')

  changeSlide = slideIndex => this.onCorePlayerReady(CPC => CPC.changeSlide(slideIndex), 'changeSlide', [slideIndex])
  changeTime = newTime => this.onCorePlayerReady(CPC => CPC.changeTime(newTime), 'changeTime', [newTime])


  scrubbingStart(newTime, slideIndex) {
    if (this.scrubState === SCRUB_STATE.IDLE) {
      this.scrubState = SCRUB_STATE.UPDATING
      this.onCorePlayerReady(
        CPC =>
          this.updatePlayer().then(() => {
            if (this.scrubState === SCRUB_STATE.UPDATING) {
              this.scrubState = SCRUB_STATE.SCRUBING
              CPC.scrubbingStart(newTime, slideIndex)
            }
          }),
        'scrubbingStart',
        [newTime, slideIndex]
      )
    }
  }

  scrubbing(newTime, slideIndex) {
    this.onCorePlayerReady(
      CPC => {
        if (this.scrubState === SCRUB_STATE.SCRUBING) {
          CPC.scrubbing(newTime, slideIndex)
        }
      },
      'scrubbing',
      [newTime, slideIndex]
    )
  }

  scrubbingEnd() {
    this.onCorePlayerReady(CPC => {
      if (this.scrubState === SCRUB_STATE.SCRUBING) {
        CPC.scrubbingEnd()
      }
      this.scrubState = SCRUB_STATE.IDLE
    }, 'scrubbingEnd')
  }

  restartPlayback = () => {
    logger.log('restart')
    this.jumpToStart(0)
    return this.play()
  }

  setShowControlsBar = () => {
    this.setState({showControlsBar: true})
    this.hideControlsBarAfterTimeout()
  }

  hideControlsBarAfterTimeout = debounce(() => {
    if (this.state.isPlaying) {
      this.setState({showControlsBar: false})
    }
  }, HIDE_CONTROLS_BAR_TIMEOUT)

  handleWindowBeforeUnload = () => {
    logger.log('handleWindowBeforeUnload')
    this.onCorePlayerReady(CPC => {
      if (CPC) {
        CPC.kill()
      }
    }, 'kill')
  }

  handleVisibilityChange = () => {
    if (document.hidden) {
      this.pause()
    }
  }

  toggleFullScreen = () => this.fullscreen.current.toggle()

  setVolume = newVolume => {
    logger.log('set volume', {newVolume})
    const {onToggleMute, dispatchWindowEvents} = this.props
    this.onCorePlayerReady(
      CPC => {
        CPC.volume = newVolume
        dispatchWindowEvents(new CustomEvent('powtoon-player-event', {detail: {type: 'toggle-mute', newVolume}}))
        onToggleMute(!!newVolume)
      },
      'volume',
      [newVolume]
    )
  }

  updateCorePlayerScale = debounce(
    (scaleX, scaleY) => {
      //lets try to update as littel as neaded core player on resize
      this.onCorePlayerReady(
        CPC => {
          logger.log('set scale', {scaleX, scaleY})
          console.log(`%c ///////////// CHANGING player scale to (${scaleX}, ${scaleY})`, 'background: red')
          CPC.resize(scaleX, scaleY)
        },
        'resize',
        [scaleX, scaleY]
      )
    },
    100,
    {trailing: true}
  )

  getPowtoonDuration = () => this.onCorePlayerReady(CPC => CPC.powtoonLength, 'powtoonLength')

  setControlsBarRef = ref => {
    this.controlsBar = ref
  }

  setSlideshowControlsRef = ref => {
    this.slideshowControls = ref
    if (this.slideshowControls) {
      this.slideshowControls.setCurrentSlide(this.currentSlideIndex)
    }
  }

  setMovieControlsRef = ref => {
    this.movieControls = ref
  }

  onFullscreenChange = isFullscreen => {
    const {isPlaying, isHorizontal} = this.state
    const {size: sizeFromProps} = this.props
    this.pause()
    if (isPlaying) {
      setTimeout(() => this.play(), 500)
    }

    if (isFullscreen) {
      this.setCorePlayerSize({
        isFullscreen,
        width: window.outerWidth,
        height: window.outerHeight
      })
      if (!isHorizontal) {
        if (IS_ANDROID) {
          this.showPhoneRotateAnimation()
          this.setState({showControlsBar: false})
        }
      }
    } else {
      // not fullScreen
      if (sizeFromProps) {
        this.setCorePlayerSize({
          isFullscreen,
          width: this.props.size.width,
          height: this.props.size.height
        })
      } else {
        this.setState({isFullscreen})
      }
    }
    if (!IS_ANDROID) {
      this.setShowControlsBar()
    }
    logger.debug('full screen change', {isFullscreen})
  }

  onResize = ({width, height}) => {
    const {isFullscreen, isHorizontal} = this.state

    if (!isHorizontal && window.outerWidth > window.outerHeight) {
      this.setState({isHorizontal: true})
    }
    if (isHorizontal && window.outerWidth < window.outerHeight) {
      this.setState({isHorizontal: false})
    }

    if (IS_IOS) {
      this.hidePhoneRotateAnimation()
    }

    this.setCorePlayerSize({
      isFullscreen,
      width,
      height
    })
  }

  setCorePlayerSize = ({width, height, isFullscreen}) => {
    const {scaleX, scaleY, stateUpdate} = this.recalcCorePlayerSizeParams(isFullscreen, {width, height})
    this.updateCorePlayerScale(scaleX, scaleY)
    this.props.setScale({scaleX, scaleY})
    this.setState(stateUpdate)
  }

  recalcCorePlayerSizeParams = (isFullscreen, containerSize = {}) => {
    const {isInStudio, doc, size = {}} = this.props

    let {width: containerWidth, height: containerHeight} = containerSize
    if (!containerWidth || !containerHeight) {
      if (isFullscreen) {
        containerWidth = window.outerWidth
        containerHeight = window.outerHeight
      } else {
        containerWidth = size.width
        containerHeight = size.height
      }
    }

    const exportAspectRatioMethod = doc ? doc.content.powtoon.exportAspectRatio.method : 'none'
    const ratio = {
      square: 1,
      none: 16 / 9,
      custom: 16 / 9,
      vertical: 9 / 16
    }

    const inscribedInContainer = isInStudio && !isFullscreen

    const contentWidth = inscribedInContainer ? SLIDE_WIDTH : SLIDE_HEIGHT * ratio[exportAspectRatioMethod]
    const verticalScale = containerWidth / contentWidth
    const horizontalScale = containerHeight / SLIDE_HEIGHT
    const scale = inscribedInContainer
      ? Math.max(verticalScale, horizontalScale)
      : Math.min(verticalScale, horizontalScale)
    const scaleX = this.roundScale(scale, SLIDE_WIDTH)
    const scaleY = this.roundScale(scale, SLIDE_HEIGHT)

    const viewWidth = contentWidth * scale
    const playerWidth = SLIDE_WIDTH * scale

    const width = inscribedInContainer ? containerWidth : playerWidth
    const height = inscribedInContainer ? containerHeight : SLIDE_HEIGHT * scale
    const horizontalMargin = (playerWidth - viewWidth) / 2

    const stateUpdate = {
      isFullscreen,
      size: {
        width,
        height,
        margin: `0 -${horizontalMargin}px`
      }
    }

    return {scaleX, scaleY, stateUpdate}
  }

  roundScale(scale, baseSize) {
    // TALK TO OFER!
    // We should round the scale in order to receive a sharp image on the final canvas.
    // This function rounds the scale so that the final pixel size will be round and even.
    let roundedScale = scale

    let canvasSize = roundedScale * baseSize
    if (canvasSize % 2 !== 0) {
      canvasSize = Math.floor(canvasSize)
      if (canvasSize % 2 !== 0) {
        canvasSize = canvasSize + 1
      }
      roundedScale = canvasSize / baseSize
    }

    roundedScale = Number(roundedScale.toFixed(5))

    return roundedScale
  }

  showPhoneRotateAnimation = () => {
    this.setState({phoneRotateAnimationVisible: true})
    setTimeout(
      this.hidePhoneRotateAnimation,
      parseFloat(styles['phone_rotate_speed']) * styles['phone_rotate_count'] * 1000
    )
  }

  hidePhoneRotateAnimation = () => {
    this.setState({phoneRotateAnimationVisible: false})
    if (IS_ANDROID) {
      this.setState({showControlsBar: true})
    }
  }

  changeAudioState = () => {
    this.onCorePlayerReady(CPC => {
      this.setState({audioState: AUDIO_STATE_RUNNING_STATE})
      CPC.resumePlayback()
    }, 'resumePlayback')
  }

  hideOverlay = () => this.setState({isOverlayVisible: false})

  render() {
    const {
      toggleFullScreen,
      setVolume,
      setControlsBarRef,
      setSlideshowControlsRef,
      setMovieControlsRef,
      play,
      pause,
      restartPlayback,
      onResize,
      onFullscreenChange,
      setShowControlsBar,
      currentTime,
      currentSlideTime,
      currentSlideIndex,
      isEnd
    } = this
    const {
      corePlayerConnectionInstance,
      fullscreenEnabled,
      isFullscreen,
      phoneRotateAnimationVisible,
      showControlsBar,
      isPlaying,
      isUpdating,
      showPreloader,
      size,
      slidesDurations,
      audioState,
      autoplay,
      isHorizontal,
      isOverlayVisible
    } = this.state
    const {
      showSpinnerWhenLoading,
      slideshowMode,
      hideControls,
      corePlayerStyle,
      forceAllowFullscreen,
      size: sizeFromProps,
      overlayContainer,
      isDisableMovieControls,
      hideFullScreenButton,
      hideVolumeControl,
      doc,
      locales,
      initialCaptionLocale
    } = this.props

    const showMuteButton = audioState === AUDIO_STATE_SUSPENDED_STATE && bowser.mobile && autoplay
    const getControls = (
      <Controls
        {...{
          toggleFullScreen: IS_IOS ? this.showPhoneRotateAnimation : toggleFullScreen,
          setVolume,
          corePlayerConnection: corePlayerConnectionInstance,
          fullscreenEnabled,
          isFullscreen,
          showControlsBar,
          currentSlideIndex,
          currentSlideTime,
          currentTime,
          isPlaying,
          isEnd,
          isUpdating,
          slideshowMode,
          hideControls,
          setControlsBarRef,
          setSlideshowControlsRef,
          hideFullScreenButton: (IS_IOS && isHorizontal) || hideFullScreenButton,
          adjustedCurrentSlideTotalTime: adjustedCurrentSlideTotalTime({currentSlideIndex, slidesDurations}),
          setMovieControlsRef,
          play,
          pause,
          numberOfSlides: this.numberOfSlides,
          totalDuration: this.totalDuration,
          showLogo: !(IS_ANDROID && !isHorizontal && isFullscreen) && !(hideFullScreenButton && hideVolumeControl),
          isDisableMovieControls,
          hideVolumeControl,
          captionLocales: locales || doc?.content?.powtoon?.captionLocales,
          setActiveCaptionLocale: this.setActiveCaptionLocale,
          initialCaptionLocale
        }}
      />
    )

    const OverlayComponent = (
      <Overlay
        onPauseAnimationEnd={this.hideOverlay}
        isOverlayVisible={isOverlayVisible}
        onPlay={isEnd ? restartPlayback : play}
        key="OverlayComponent"
      />
    )

    return (
      <FullscreenToggle
        key="FullscreenToggle"
        ref={this.fullscreen}
        onFullscreenChange={onFullscreenChange}
        forceAllowFullscreen={forceAllowFullscreen}
      >
        <div
          styleName="styles.player"
          data-id="player" id="player"
          data-testid="player"
          onFocus={this.setShowControlsBar}
          aria-label="Player"
          role="region"
        >
          {showSpinnerWhenLoading && showPreloader && <Preloader />}
          {showMuteButton && <div className={styles['tap-to-unmute-button']} onClick={this.changeAudioState} />}
          <div styleName={`styles.core-player-container${hideControls ? '-no-controls' : ''}`}>
            {!sizeFromProps && <AutoSizer onResize={onResize}>{noop}</AutoSizer>}
            <div
              styleName="styles.core-player"
              style={size}
              onMouseMove={setShowControlsBar}
              onTouchStart={setShowControlsBar}
            >
              <div
                style={corePlayerStyle}
                ref={this.corePlayerContainerRef}
                data-id="player-visible-area"
                styleName="styles.player-visible-area"
                tabIndex={0}
              />
              <Animate transitionName={fadeEffect}>{getControls}</Animate>
            </div>
          </div>
          {/* TODO: once merged, use powtoon-components Portal Component */}
          {overlayContainer ? ReactDOM.createPortal([OverlayComponent], overlayContainer) : OverlayComponent}
          {phoneRotateAnimationVisible && (
            <div styleName="styles.animation_background">
              <div styleName="styles.phone_rotate_animation" />
            </div>
          )}
        </div>
      </FullscreenToggle>
    )
  }
}
